import { useCallback } from "react";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { Popover, PopoverTrigger } from "@/components/molecules/popover";
import { cn } from "@/lib/utils";
import type { DirectiveChipStrategy } from "../../directives/core/chipStrategy";
import { SEGMENT_CLASSES } from "../variableTokenConstants";
import { useDirectiveChipNodeView } from "./useDirectiveChipNodeView";
import type { DirectiveChipEditSegment } from "../../types/directiveChipTypes";
import { DirectiveChipTrigger } from "./directiveChipTrigger";
import { DirectiveChipPopoverContent } from "./directiveChipPopoverContent";

export type VariableTokenNodeViewDirectiveProps = NodeViewProps & {
  strategy?: DirectiveChipStrategy | null;
};

/**
 * Directive chip: segment chip; when incomplete, popover shows variable picker or choice input(s).
 * Lifecycle: incomplete directives auto-open the popover; completing a variable or last choice
 * moves focus back into the editor after the node.
 */
export const VariableTokenNodeViewDirective = ({
  node,
  editor,
  getPos,
  strategy = null,
}: VariableTokenNodeViewDirectiveProps) => {
  const chip = useDirectiveChipNodeView({
    node,
    editor,
    getPos,
    strategy,
  });

  const {
    parsed,
    config,
    allVariables,
    open,
    setOpen,
    editSegment,
    setEditSegment,
    chipPickerIndex,
    setChipPickerIndex,
    chipPickerKeyboardRef,
    preventCloseOnceRef,
    firstVariableButtonRef,
    choiceInputRef,
    choiceSelectTriggerRef,
    filteredVariables,
    availableDirectives,
    needsVariable,
    needsNextChoice,
    nextChoiceSlot,
    choicesPart,
    directiveLabel,
    variablePart,
    getChoiceSlotLabel,
    handlePopoverContentOpenAutoFocus,
    handlePopoverCloseAutoFocus,
    chipSegmentFilterText,
    handleChipSegmentFilterChange,
    chipSegmentFilterInputRef,
    handleSelectDirective,
    handleUpdateVariableOnly,
    handleSelectVariable,
    handleChoiceSubmit,
    handleUpdateChoiceOnly,
    handleReorderPrintTableChoices,
    handleSegmentClick,
  } = chip;

  const segmentButton = useCallback(
    (
      segment: DirectiveChipEditSegment,
      label: string,
      placeholderStyle = false,
    ) => (
      <span
        role="button"
        tabIndex={0}
        className={cn(
          SEGMENT_CLASSES,
          `
            cursor-pointer
            hover:bg-secondary/80
          `,
          placeholderStyle && "text-foreground/45",
        )}
        onClick={(e) => {
          e.stopPropagation();
          handleSegmentClick(segment);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            handleSegmentClick(segment);
          }
        }}
      >
        {label}
      </span>
    ),
    [handleSegmentClick],
  );

  if (parsed == null) return null;

  return (
    <NodeViewWrapper as="span" contentEditable={false} className="inline">
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setEditSegment(null);
        }}
      >
        <PopoverTrigger asChild>
          <DirectiveChipTrigger
            directiveLabel={directiveLabel}
            variablePart={variablePart}
            choicesPart={choicesPart}
            strategy={strategy}
            segmentButton={segmentButton}
            onReorderPrintTableChoices={handleReorderPrintTableChoices}
            onToggleOpenFromKeyboard={() => {
              setOpen((prev) => !prev);
            }}
          />
        </PopoverTrigger>
        <DirectiveChipPopoverContent
          editSegment={editSegment}
          parsed={parsed}
          config={config}
          strategy={strategy}
          allVariables={allVariables}
          choicesPart={choicesPart}
          directiveLabel={directiveLabel}
          filteredVariables={filteredVariables}
          availableDirectives={availableDirectives}
          needsVariable={needsVariable}
          needsNextChoice={needsNextChoice}
          nextChoiceSlot={nextChoiceSlot}
          chipPickerIndex={chipPickerIndex}
          setChipPickerIndex={setChipPickerIndex}
          setOpen={setOpen}
          chipSegmentFilterText={chipSegmentFilterText}
          onChipSegmentFilterChange={handleChipSegmentFilterChange}
          chipSegmentFilterInputRef={chipSegmentFilterInputRef}
          chipPickerKeyboardRef={chipPickerKeyboardRef}
          firstVariableButtonRef={firstVariableButtonRef}
          choiceInputRef={choiceInputRef}
          choiceSelectTriggerRef={choiceSelectTriggerRef}
          preventCloseOnceRef={preventCloseOnceRef}
          getChoiceSlotLabel={getChoiceSlotLabel}
          onOpenAutoFocus={handlePopoverContentOpenAutoFocus}
          onCloseAutoFocus={handlePopoverCloseAutoFocus}
          onSelectDirective={handleSelectDirective}
          onUpdateVariableOnly={handleUpdateVariableOnly}
          onSelectVariable={handleSelectVariable}
          onChoiceSubmit={handleChoiceSubmit}
          onUpdateChoiceOnly={handleUpdateChoiceOnly}
        />
      </Popover>
    </NodeViewWrapper>
  );
};
